// The Items and stock page (docs/cockpit/spec.md): what the business sells or buys, by name, with the stock on
// hand the person may see, filtered by whether it is in stock and searched by the server. "Add" and each row's
// "Edit" open the same form chat's drafts use, with an Item's opening stock, behind the same commands and Roles (#502).
// Each row's "BOM" opens the Item's bill of materials and routing, given or corrected there by whoever may (#565),
// and its "Files" the files kept against it (#575).
import { useCallback, useState } from "react";
import { clearing, type Api, type ItemRow, type StockShow } from "../api.js";
import { Dialog } from "../Dialog.js";
import { formatNumber } from "../i18n/format.js";
import type { Language } from "../i18n/language.js";
import { useLanguage, useWords, type Words } from "../i18n/words.js";
import { ListSearch, ListView, Segments, useList } from "../lists/List.js";
import { KeptFiles } from "../files/KeptFiles.js";
import { MasterDataForm } from "../parties/MasterDataForm.js";
import { ItemBom } from "./ItemBom.js";

/** Paise as rupees, or a dash where there is none. */
const rupees = (paise: number | null | undefined, language: Language) =>
  paise == null
    ? "—"
    : `₹${formatNumber(paise / 100, language, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const quantity = (value: number, language: Language) => formatNumber(value, language, { maximumFractionDigits: 3 });

const shows = (w: Words): readonly (readonly [StockShow, string])[] => [
  ["all", w.itemsAll],
  ["in_stock", w.itemsInStock],
  ["out_of_stock", w.itemsOutOfStock],
];

export function Items({
  api,
  initialQuery = "",
}: {
  /** Without `saveItem`, the page only lists; without `itemBom`, it shows no BOM; without `keptFiles`, no files. */
  api: Pick<Api, "items"> & Partial<Pick<Api, "saveItem" | "itemBom" | "giveItemBom" | "keptFiles">>;
  initialQuery?: string;
}) {
  const w = useWords();
  const { language } = useLanguage();
  const [stock, setStock] = useState<StockShow>("all");
  const [q, setQ] = useState(initialQuery);
  // Saving reads the list again: a new loader each time.
  const [version, setVersion] = useState(0);
  const load = useCallback<Api["items"]>((filters) => api.items(filters), [api, version]);
  const { loaded, setPage } = useList(load, { stock, q });
  const [editing, setEditing] = useState<ItemRow | "new">();
  const [bomOf, setBomOf] = useState<ItemRow>();
  const [filesOf, setFilesOf] = useState<ItemRow>();
  const save = api.saveItem;
  const itemBom = api.itemBom;
  const keptFiles = api.keptFiles;
  return (
    <>
      <div className="hello">
        <h2>{w.itemsTitle}</h2>
        <p className="muted">{w.itemsIntro}</p>
      </div>
      <div className="toolbar">
        <Segments label={w.itemsShow} options={shows(w)} value={stock} onChange={setStock} />
        <ListSearch label={w.itemsSearch} initial={initialQuery} onSearch={setQ} />
        {save ? (
          <button type="button" className="btn" onClick={() => setEditing("new")}>
            {w.masterAdd}
          </button>
        ) : null}
      </div>
      <ListView loaded={loaded} what={w.itemsWhat} empty={q ? w.itemsNoMatch(q) : w.itemsNone} onPage={setPage}>
        {(rows) => {
          // A cost or price the person's Roles hide is left out of the page, never shown as missing (#1013).
          const costs = rows.some((r) => r.purchaseRate !== undefined);
          const prices = rows.some((r) => r.salePrice !== undefined);
          const held = rows.some((r) => r.heldForOrders != null);
          const rates = (row: ItemRow) =>
            costs && prices
              ? row.purchaseRate == null && row.salePrice == null
                ? "—"
                : w.itemsRatesOf(rupees(row.purchaseRate, language), rupees(row.salePrice, language), row.unit)
              : (costs ? row.purchaseRate : row.salePrice) == null
                ? "—"
                : w.itemsRateOf(rupees(costs ? row.purchaseRate : row.salePrice, language), row.unit);
          return (
            <table className="tbl">
              <thead>
                <tr>
                  <th scope="col">{w.itemsItem}</th>
                  <th scope="col">{w.itemsHsn}</th>
                  <th scope="col" className="r">
                    {w.itemsGstRate}
                  </th>
                  <th scope="col" className="r">
                    {w.itemsStock}
                  </th>
                  {held ? (
                    <th scope="col" className="r">
                      {w.itemsHeld}
                    </th>
                  ) : null}
                  {costs || prices ? (
                    <th scope="col" className="r">
                      {costs && prices ? w.itemsRates : costs ? w.itemsCost : w.itemsSalePrice}
                    </th>
                  ) : null}
                  <th scope="col" className="r">
                    {w.itemsReorder}
                  </th>
                  {save || itemBom || keptFiles ? (
                    <th scope="col">
                      <span className="sr-only">{w.masterActions}</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <th scope="row">{row.name}</th>
                    <td>{row.hsn ?? "—"}</td>
                    <td className="r">{row.gstRate === null ? "—" : `${String(row.gstRate)}%`}</td>
                    <td className="r">
                      {row.stock === null
                        ? "—"
                        : `${formatNumber(row.stock, language, { maximumFractionDigits: 3 })} ${row.unit}`}
                    </td>
                    {held ? (
                      <td className="r">
                        {row.heldForOrders == null ? "—" : `${quantity(row.heldForOrders, language)} ${row.unit}`}
                      </td>
                    ) : null}
                    {costs || prices ? <td className="r">{rates(row)}</td> : null}
                    <td className="r">
                      {row.reorderLevel == null
                        ? "—"
                        : w.itemsReorderOf(
                            quantity(row.reorderLevel, language),
                            row.minOrderQuantity == null ? undefined : quantity(row.minOrderQuantity, language),
                            row.unit,
                          )}
                    </td>
                    {save || itemBom || keptFiles ? (
                      <td>
                        {save ? (
                          <button
                            type="button"
                            className="btn"
                            aria-label={w.masterEditOf(row.name)}
                            onClick={() => setEditing(row)}
                          >
                            {w.masterEdit}
                          </button>
                        ) : null}
                        {itemBom ? (
                          <button
                            type="button"
                            className="btn"
                            aria-label={w.bomOf(row.name)}
                            onClick={() => setBomOf(row)}
                          >
                            {w.bomButton}
                          </button>
                        ) : null}
                        {keptFiles ? (
                          <button
                            type="button"
                            className="btn"
                            aria-label={w.keptFilesOf(row.name)}
                            onClick={() => setFilesOf(row)}
                          >
                            {w.keptFilesButton}
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }}
      </ListView>
      {save && editing ? (
        <Dialog
          title={editing === "new" ? w.masterAddItem : w.masterEditOf(editing.name)}
          onClose={() => setEditing(undefined)}
        >
          <MasterDataForm
            record="item"
            editing={editing !== "new"}
            initial={
              editing === "new"
                ? {}
                : {
                    name: editing.name,
                    ...(editing.hsn ? { hsn: editing.hsn } : {}),
                    ...(editing.gstRate === null ? {} : { gstRate: editing.gstRate }),
                    ...(editing.kind ? { itemKind: editing.kind } : {}),
                  }
            }
            submitLabel={w.masterSave}
            onSubmit={async (fields, _kind, emptied) => {
              // Editing, a kind, HSN or GST rate the person emptied is cleared on record, not kept.
              const saved = await (editing === "new"
                ? save(fields)
                : save({ ...fields, ...clearing(emptied) }, editing.id));
              setVersion((v) => v + 1);
              return saved.text;
            }}
          />
        </Dialog>
      ) : null}
      {itemBom && bomOf ? (
        <Dialog title={w.bomOf(bomOf.name)} onClose={() => setBomOf(undefined)}>
          <ItemBom api={{ itemBom, ...(api.giveItemBom ? { giveItemBom: api.giveItemBom } : {}) }} item={bomOf.id} />
        </Dialog>
      ) : null}
      {keptFiles && filesOf ? (
        <Dialog title={w.keptFilesOf(filesOf.name)} onClose={() => setFilesOf(undefined)}>
          <KeptFiles api={{ keptFiles }} object="item" record={filesOf.id} />
        </Dialog>
      ) : null}
    </>
  );
}
